import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";
import { Tooltip } from "../../../../../common/components/Tooltip/Tooltip";

interface ContentQuizz3Data {
  question_text: string;
  list_answers: string[];
  correct_answers: boolean[];
  has_multiple_answers?: boolean;
  explanation_true_text?: string;
  explanation_false_text?: string;
}

interface Quiz3DetailProps {
  data: {
    list_selected_answers_indexes?: number[];
    score?: number;
  };
  contentId?: number;
}

const Quiz3Detail = ({ data, contentId }: Quiz3DetailProps) => {
  const { t } = useTranslation();
  const [quizData, setQuizData] = useState<ContentQuizz3Data | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  const selectedAnswersIndexes = data.list_selected_answers_indexes || [];

  // Fetch content-quizz-3 to get question data
  useEffect(() => {
    const fetchQuizData = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        const response = await axios.get(
          `${apiUrls.stats.contentSpecificData}?contentId=${contentId}&contentType=content-quizz-3`,
        );

        // Note: axios interceptor in auth.ts auto-unwraps response.data.data to response.data
        const quizz3Data = response.data;

        if (quizz3Data) {
          setQuizData({
            question_text: quizz3Data.question_text,
            list_answers: quizz3Data.list_answers || [],
            correct_answers: quizz3Data.correct_answers || [],
            has_multiple_answers: quizz3Data.has_multiple_answers,
            explanation_true_text: quizz3Data.explanation_true_text,
            explanation_false_text: quizz3Data.explanation_false_text,
          });
        }
      } catch (err) {
        console.error("Error fetching quiz content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchQuizData();
  }, [contentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!quizData || quizData.list_answers.length === 0) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  // Check if an answer was selected
  const wasAnswerSelected = (answerIndex: number): boolean => {
    return selectedAnswersIndexes.includes(answerIndex);
  };

  // Calculate if user answered perfectly by comparing selected answers with correct answers
  // Perfect score = all correct answers selected AND no wrong answers selected
  const isPerfectScore = quizData.list_answers.every((_, index) => {
    const isCorrectAnswer = quizData.correct_answers[index] === true;
    const wasSelected = wasAnswerSelected(index);
    // Correct if: (correct answer AND selected) OR (wrong answer AND not selected)
    return isCorrectAnswer === wasSelected;
  });

  const explanation = (
    isPerfectScore ? quizData.explanation_true_text : quizData.explanation_false_text
  )?.trim();

  return (
    <div className="h-full flex flex-col">
      <div className="border-2 border-teal-200 rounded-lg p-4 flex-1 overflow-y-auto">
        {/* Question */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Question header */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-start gap-2">
              <span className="text-sm font-medium text-gray-500">Question 1</span>
              {quizData.has_multiple_answers && (
                <span className="text-xs text-gray-400 italic">
                  {t("statsNew.interaction.quiz.multipleChoice")}
                </span>
              )}
            </div>
            <p className="mt-2 font-medium text-gray-900">{quizData.question_text}</p>
          </div>

          {/* Answers */}
          <div className="p-4 space-y-2">
            {quizData.list_answers.map((answerText, aIndex) => {
              const answerIsCorrect = quizData.correct_answers[aIndex] === true;
              const wasSelected = wasAnswerSelected(aIndex);
              const isCorrectAndSelected = answerIsCorrect && wasSelected;
              const isWrongAndSelected = !answerIsCorrect && wasSelected;
              const isMissedCorrect = answerIsCorrect && !wasSelected;

              // Determine styling
              let bgColor = "bg-white border-gray-200";
              let textColor = "text-gray-700";

              if (isCorrectAndSelected) {
                bgColor = "bg-green-100 border-green-500 border-2";
                textColor = "text-green-800 font-medium";
              } else if (isWrongAndSelected) {
                bgColor = "bg-red-100 border-red-400";
                textColor = "text-red-800";
              } else if (isMissedCorrect) {
                bgColor = "bg-amber-50 border-amber-300 border-dashed";
                textColor = "text-amber-700";
              }

              return (
                <div
                  key={aIndex}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${bgColor}`}
                >
                  {/* Status icon */}
                  <div className="flex-shrink-0">
                    {isCorrectAndSelected && (
                      <Tooltip message={t("statsNew.interaction.quiz.tooltipCorrect")}>
                        <svg
                          className="h-5 w-5 text-green-600 cursor-help"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </Tooltip>
                    )}
                    {isWrongAndSelected && (
                      <Tooltip message={t("statsNew.interaction.quiz.tooltipWrong")}>
                        <svg
                          className="h-5 w-5 text-red-600 cursor-help"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </Tooltip>
                    )}
                    {isMissedCorrect && (
                      <Tooltip message={t("statsNew.interaction.quiz.tooltipMissed")}>
                        <svg
                          className="h-5 w-5 text-amber-500 cursor-help"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                      </Tooltip>
                    )}
                    {!wasSelected && !answerIsCorrect && <div className="w-5 h-5" />}
                  </div>

                  {/* Answer text */}
                  <span className={`text-sm ${textColor}`}>{answerText}</span>
                </div>
              );
            })}
          </div>

          {/* Explanation */}
          {explanation && (
            <div className="px-4 pb-4">
              <p className="text-xs font-medium text-gray-500 mb-1">
                {t("statsNew.interaction.explanation")}
              </p>
              <div className={`p-3 rounded-lg ${isPerfectScore ? "bg-green-50" : "bg-red-50"}`}>
                <p className={`text-sm ${isPerfectScore ? "text-green-700" : "text-red-700"}`}>
                  {explanation}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Quiz3Detail;
